import { ref } from 'vue';

/**
 * Whether the sign-out confirmation is on screen.
 *
 * ── Why a module-scope flag and not a prop ───────────────────────────────────
 *
 * Sign out is raised from three places that share no parent worth threading a
 * prop through: the garage title bar, the compact settings sheet (which is
 * rendered into `SettingsPanel`'s `actions` slot, so it is a *grandchild* of the
 * garage), and `VerifyEmailGate`, which is a different screen entirely. One
 * boolean read by one dialog mounted near the root in `App.vue` is what
 * `state/confirm-purchase.ts` already does for a spend, and for the same reason:
 * a fourth caller inherits the dialog by calling one function.
 *
 * ── Why the dialog performs the logout, not this module ──────────────────────
 *
 * `ConfirmPurchase` resolves a promise the caller is awaiting, because a
 * purchase's *consequence* differs per call site. Signing out has exactly one
 * consequence — `session.logout()` — so there is nothing to hand back, and a
 * store that imported the session to call it would make a two-line flag depend
 * on the whole auth module. The dialog owns the action; this owns the answer to
 * "is it being asked".
 */
const asked = ref(false);

/** Read by `ui/ConfirmSignOut.vue`. */
export const signOutAsked = asked;

/** Opens the confirmation. Every Sign out control calls this instead of logging out. */
export function askSignOut(): void {
  asked.value = true;
}

/**
 * Closes it without signing out.
 *
 * Called by Cancel, by the backdrop and by Escape — the safe answer has to be
 * the easy one, which is the rule the purchase dialog states.
 */
export function cancelSignOut(): void {
  asked.value = false;
}
